import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Repeat, Zap, Trash2, AlertCircle, Calendar } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminSubscriptions() {
    const [rows, setRows] = useState([]);
    const [tab, setTab] = useState("all");
    const [busyId, setBusyId] = useState("");

    const load = async () => {
        const all = await api.get("/admin/subscriptions");
        setRows(all.data);
    };
    useEffect(() => { load(); }, []);

    const runNow = async (s) => {
        if (!window.confirm(`Create the next refill order for ${s.user_name || s.user_email} (${s.product_name})?`)) return;
        setBusyId(s.id);
        try {
            const { data } = await api.post(`/admin/subscriptions/${s.id}/run-now`);
            toast.success(`Refill placed · next on ${data.next_run}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail); }
        finally { setBusyId(""); }
    };
    const cancel = async (s) => {
        if (!window.confirm(`Cancel ${s.user_name}'s subscription to ${s.product_name}?`)) return;
        try { await api.delete(`/admin/subscriptions/${s.id}`); toast.success("Cancelled"); await load(); } catch (e) { toast.error(e.response?.data?.detail); }
    };

    const filtered = rows.filter(r => {
        if (tab === "all") return true;
        if (tab === "due") return r.status === "active" && r.next_run_due;
        return r.status === tab;
    });
    const counts = {
        active: rows.filter(r => r.status === "active").length,
        due: rows.filter(r => r.status === "active" && r.next_run_due).length,
        paused: rows.filter(r => r.status === "paused").length,
        cancelled: rows.filter(r => r.status === "cancelled").length,
    };

    return (
        <div className="space-y-5" data-testid="admin-subscriptions-page">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Customer success</div>
                <h1 className="font-serif text-4xl text-stone-900 inline-flex items-center gap-3"><Repeat className="w-7 h-7 text-sky-700" />Auto-refill subscriptions</h1>
                <p className="text-sm text-stone-500 mt-1">Chronic patients who've signed up for scheduled refills. Click <em>Refill now</em> on any due subscription to manually place the next order.</p>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-stone-200">
                {[
                    { k: "all", n: rows.length, label: "All" },
                    { k: "due", n: counts.due, label: "Due", colour: "text-amber-700" },
                    { k: "active", n: counts.active, label: "Active" },
                    { k: "paused", n: counts.paused, label: "Paused" },
                    { k: "cancelled", n: counts.cancelled, label: "Cancelled" },
                ].map(t => (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`sub-tab-${t.k}`}
                        className={`px-4 py-3 -mb-px text-sm border-b-2 ${tab === t.k ? "border-sky-700 text-sky-700 font-semibold" : `border-transparent ${t.colour || "text-stone-500"} hover:text-stone-900`}`}>
                        {t.label}<span className="ml-1 text-[10px]">({t.n})</span>
                    </button>
                ))}
            </div>

            {counts.due > 0 && tab !== "due" && (
                <div className="bg-amber-50 border border-amber-200 p-3 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <span><strong>{counts.due}</strong> subscription{counts.due === 1 ? " is" : "s are"} due for refill.</span>
                    <button onClick={() => setTab("due")} className="ml-auto text-amber-800 underline hover:no-underline text-xs">Show due only →</button>
                </div>
            )}

            {filtered.length === 0 ? (
                <div className="bg-white border border-stone-200 p-12 text-center text-sm text-stone-500">
                    <Repeat className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    No subscriptions in this view.
                </div>
            ) : (
                <div className="bg-white border border-stone-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Customer</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Product</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Frequency</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Next refill</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Orders</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Per refill</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                        </tr></thead>
                        <tbody>
                            {filtered.map(s => (
                                <tr key={s.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`admin-sub-${s.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900">{s.user_name || "—"}</div>
                                        <div className="text-[10px] text-stone-500 font-mono">{s.user_email}</div>
                                    </td>
                                    <td className="p-3">
                                        <Link to={`/product/${s.product_slug}`} className="font-medium text-stone-900 hover:text-sky-700">{s.product_name}</Link>
                                        <div className="text-[10px] text-stone-500">{s.size_label} · qty {s.quantity}</div>
                                    </td>
                                    <td className="p-3 text-xs text-stone-600">{s.cadence_label}</td>
                                    <td className="p-3 text-xs">
                                        <span className={s.next_run_due && s.status === "active" ? "text-amber-700 font-semibold" : "text-stone-700"}>
                                            <Calendar className="inline w-3 h-3 mr-1" />{s.next_run || "—"}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right font-mono">{s.orders_count || 0}</td>
                                    <td className="p-3 text-right font-mono text-emerald-700">{formatINR((s.unit_price || 0) * (s.quantity || 1))}</td>
                                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${s.status === "active" ? "bg-emerald-100 text-emerald-800" : s.status === "paused" ? "bg-amber-100 text-amber-800" : "bg-stone-200 text-stone-600"}`}>{s.status}</span></td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex gap-1">
                                            {s.status === "active" && (
                                                <Button size="sm" variant="outline" disabled={busyId === s.id} className="rounded-none h-7 px-2 border-sky-300 text-sky-700" onClick={() => runNow(s)} data-testid={`admin-sub-run-${s.id}`}>
                                                    <Zap className="w-3 h-3 mr-1" />{busyId === s.id ? "…" : "Refill now"}
                                                </Button>
                                            )}
                                            {s.status !== "cancelled" && (
                                                <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-red-300 text-red-700" onClick={() => cancel(s)} data-testid={`admin-sub-cancel-${s.id}`}>
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
